'use client';

import { memo as Memo, useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { gsap } from 'gsap';
import dayjs from 'dayjs';

//* HOC's
import withUIContext from '@context/consumerHOC/UIConsumer';
import withLanguageContext from '@/context/consumerHOC/LanguageConsumer';

//* JSONs
import config from '@lib/config.json';

//* Components
import Page from '@components/global/Page';
import TableItem from '@components/global/TableItem';
import AwardsCont from '@components/global/AwardsCont';
import HTMLContainer from '@components/global/HTMLContainer';
import PageTopSection from '@components/global/PageTopSection';
import CustomLink from '@components/global/CustomLink';
import Button from '@components/global/Button';
import Image from '@components/global/Image';
import Text from '@/components/global/Text';

//* SVGs
import ZiggyEyes from '@components/global/SvgComponents/ZiggyEyes';
import RollyEyes from '@components/global/SvgComponents/RollyEyes';
import SharpyEyes from '@components/global/SvgComponents/SharpyEyes';
import PeezzyEyes from '@components/global/SvgComponents/PeezzyEyes';

//* Styles
import ServicesStyle from '@/styles/pages/ServicesStyle';
import GameEyes from '@/components/global/SvgComponents/GameEyes';
import AppEyes from '@/components/global/SvgComponents/AppEyes';
import SeoEyes from '@/components/global/SvgComponents/SeoEyes';

const Services = Memo(({ pageData, winHeight, portfolioData, translate }) => {
	//! States
	const [hoverKey, setHoverKey] = useState();
	const [secObj, setSecObj] = useState({});

	//! Portfolio items by type
	const portfolioItemsByType = useMemo(() => {
		let obj = {};

		if (portfolioData) {
			const items = Object.values(portfolioData.items);

			for (let i = 0; i < items.length; i++) {
				obj = {
					...obj,
					[items[i].fields.serviceType]: obj?.[items[i].fields.serviceType] ? [...obj[items[i].fields.serviceType], items[i]] : [items[i]],
				};
			}
		}
		return obj;
	}, [portfolioData]);

	//! Animation
	const tl = gsap.timeline({});

	//! Refs
	const revealRefs = useRef([]);
	revealRefs.current = [];

	const addToRefs = (el) => {
		if (el && !revealRefs.current.includes(el)) {
			revealRefs.current.push(el);
		}
	};

	//! Section Animation
	const sectionAnimation = useCallback(() => {
		if (revealRefs.current) {
			tl.clear();
			let newObj = { ...secObj };
			let isChange = false;

			for (let i = 0; i < revealRefs.current.length; i++) {
				const el = revealRefs.current[i];

				const secIsActive = el?.getBoundingClientRect().top < winHeight;
				newObj = { ...newObj, [`sec${i + 1}`]: secIsActive };

				if (secObj[`sec${i + 1}`] && !newObj[`sec${i + 1}`]) {
					isChange = true;

					if (revealRefs.current.length - 1 !== i) {
						tl.staggerTo([el.lastChild.childNodes], 0, {
							opacity: 0,
						}).play();
					}
				}

				if (!secObj[`sec${i + 1}`] && newObj[`sec${i + 1}`]) {
					isChange = true;

					if (revealRefs.current.length - 1 !== i) {
						tl.staggerTo(
							[el.lastChild.childNodes],
							0,
							{
								opacity: 1,
								delay: 0.8,
							},
							0.1
						).play();
					}
				}
			}

			isChange && setSecObj(newObj);
		}
	}, [revealRefs, secObj, tl, winHeight]);

	useEffect(() => {
		window.addEventListener('scroll', sectionAnimation);

		return () => {
			window.removeEventListener('scroll', sectionAnimation);
		};
	}, [sectionAnimation]);

	const animeComp = useMemo(() => {
		return {
			'E-commerce': <ZiggyEyes />,
			'Branding': <PeezzyEyes />,
			'Digital': <SharpyEyes />,
			'Product': <RollyEyes />,

			'Game': <GameEyes />,
			'App': <AppEyes />,
			'Seo': <SeoEyes />,
		};
	}, []);

	return (
		<Page>
			<ServicesStyle>
				<HTMLContainer>
					<PageTopSection {...pageData.hero} />

					<div className={'articleWrap'}>
						{Object.values(pageData.services).map((item, ind) => {
							const itemType = item.type;

							return (
								itemType && (
									<div
										key={ind}
										ref={addToRefs}
										className={'article-item section-item'}>
										<TableItem
											count
											minWidth
											underline
											minContent
											animationDelay={0.05}
											extensions={item.children}
											description={item.subtitle}
											url={`/services/${item.slug}`}
											animation={animeComp[itemType]}
											active={secObj[`sec${ind + 1}`]}
											title={item.title.replace('</br>', '')}
										/>

										{portfolioItemsByType[itemType] ? (
											<div className={'portfolioWrap'}>
												{portfolioItemsByType[itemType].slice(0, 2).map((portfolioItem, index) => {
													return (
														<div
															key={index}
															onMouseLeave={setHoverKey}
															className={'portfolioItem'}
															onMouseEnter={() => setHoverKey(index)}>
															<CustomLink url={`${config.routes.default.portfolio.path}${portfolioItem.fields.slug}`}>
																<div className={'image_video_cont'}>
																	{hoverKey === index ? (
																		<video
																			muted
																			loop
																			autoPlay
																			src={portfolioItem.fields.shortVideoLandscape.fields.file.url}
																		/>
																	) : null}
																	{
																		<Image
																			src={portfolioItem.fields.smallImageLandscape.fields.file.url}
																			alt={portfolioItem.fields.smallImageLandscape.fields.title}
																			className={'image_video'}
																		/>
																	}
																</div>

																<Text
																	tag={'p'}
																	className={`h6 font-poppins font-medium title`}>
																	{portfolioItem.fields.title}
																</Text>

																<Text className={`p-m font-poppins font-regular description`}>{portfolioItem.fields.workType}</Text>

																<Text className={`p-m font-poppins font-italic createdDate`}>{dayjs(portfolioItem.fields.createdAt).format('YYYY')}</Text>
															</CustomLink>
														</div>
													);
												})}

												<div className={'moreInfoLink'}>
													<Button
														text={translate('moreWorks')}
														otherLink={{
															pathname: config.routes.default.portfolio.path,
														}}
													/>
												</div>
											</div>
										) : null}
									</div>
								)
							);
						})}
					</div>
				</HTMLContainer>

				<div
					ref={addToRefs}
					className='section-item crStick'>
					<AwardsCont
						type={1}
						count={4}
						link={'/awards/'}
						data={pageData.awards}
						active={secObj[`sec${Object.keys(secObj).length}`]}
					/>
				</div>
			</ServicesStyle>
		</Page>
	);
});

export default withUIContext(withLanguageContext(Services, ['translate']), ['winHeight']);
